import { FormEventHandler, useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler, UseFormReturn, useForm } from "react-hook-form";
import { Toast } from "@sscs/ui";
import { apiErrorToast, validatePassword } from "Utils";
import * as yup from "Utils/yup";
import { useRegisterUserMutation } from "../../services/users.api";
import { RegisterUserOnSuccess } from "../../types";

type RegistrationFormData = {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
};

type UseRegistrationForm = {
  onSubmit: FormEventHandler<HTMLFormElement>;
  form: UseFormReturn<RegistrationFormData>;
};

const schema: yup.SchemaOf<RegistrationFormData> = yup.object({
  email: yup.string().required(),
  firstName: yup.string().required(),
  lastName: yup.string().required(),
  password: validatePassword().required(),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords must match")
    .required(),
});

const useRegisterMemberForm = (
  onSuccess: RegisterUserOnSuccess,
): UseRegistrationForm => {
  const defaultValues = useMemo(
    () => ({
      email: "",
      firstName: "",
      lastName: "",
      password: "",
      confirmPassword: "",
    }),
    [],
  );

  const [registerMember] = useRegisterUserMutation();

  const form = useForm<RegistrationFormData>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const { handleSubmit, reset } = form;

  const onSubmit: SubmitHandler<RegistrationFormData> = async (formData) => {
    // clone the form data so changes to form data won't affect us after reset
    const data = { ...formData };
    try {
      await registerMember({
        body: {
          email: data.email,
          first_name: data.firstName,
          last_name: data.lastName,
          password: data.password,
        },
      }).unwrap();
    } catch (error) {
      apiErrorToast(error, "Could not create account");
      return;
    } finally {
      reset(defaultValues);
    }

    onSuccess(data.email);
    Toast.show({
      title: "Account created",
      type: "success",
    });
  };

  return {
    form,
    onSubmit: handleSubmit(onSubmit),
  };
};

export default useRegisterMemberForm;
